import type NavItem from '../nav-item/NavItem.js'
import { html, LitElement, nothing } from 'lit'
import { customElement, property } from 'lit/decorators.js'

import { EventController } from '../common/controllers/EventController.js'
import { SlotController } from '../common/controllers/SlotController.js'
import componentStyle from '../common/styles/Component.css'
import style from './Navigation.css'

// Mirrors the layout's wide-viewport breakpoint. Used to drive the
// `data-rail-collapsed` attribute below — Safari doesn't support
// `:host-context()`, so we propagate the layout's rail state via an
// observed attribute on the host instead.
const railMediaQuery
  = typeof matchMedia !== 'undefined'
    ? matchMedia('(min-width: 768px)')
    : ({
        matches: false,
        addEventListener() {
          /* noop */
        },
        removeEventListener() {
          /* noop */
        },
      } as unknown as MediaQueryList)

/**
 * Navigation is used to display the primary navigation in the sidebar
 * of an application. Navigation includes a list of links that users
 * use to move between sections of the application.
 *
 * @status ready
 * @category navigation
 * @slot - The main section of the sidebar, for holding nav components.
 * @slot header - The top section of the sidebar.
 * @slot footer - The bottom section of the sidebar.
 *
 * @fires {NordEvent} toggle - Dispatched whenever a nav item's state changes between open and closed.
 * @fires {NordEvent} activate - Dispatched whenever a nav item has been marked as active
 *
 * @cssprop [--n-navigation-background-color=var(--n-color-nav-surface)] - Controls the background color of the navigation element.
 * @cssprop [--n-navigation-scroll-shadow-color-start=rgb(0, 0, 0, 0.1)] - Color of the scroll-affordance shadow shown at the top of the scroll area (below the header) when `sticky-footer` is set. Set to `transparent` to disable.
 * @cssprop [--n-navigation-scroll-shadow-color-end=rgb(0, 0, 0, 0.1)] - Color of the scroll-affordance shadow shown at the bottom of the scroll area (above the footer) when `sticky-footer` is set. Set to `transparent` to disable.
 * @cssprop [--n-navigation-scroll-mask=none] - `mask-image` applied to the scrollable area when `sticky-footer` is set. Use with the shadow colors set to `transparent` to replace the dark scroll-shadow with a soft content fade at the edges. Example: `linear-gradient(to bottom, transparent 0, black 24px, black calc(100% - 24px), transparent 100%)`.
 * @cssprop [--n-navigation-padding-block=var(--n-space-m)] - Block-axis (top/bottom) padding of the `<nav>` element that wraps the slotted nav groups.
 * @cssprop [--n-navigation-padding-inline=var(--n-space-m)] - Inline-axis (start/end) padding of the `<nav>` element that wraps the slotted nav groups.
 * @cssprop [--n-navigation-footer-padding-block=var(--n-space-s)] - Block-axis (top/bottom) padding inside the footer slot when `sticky-footer` is set. Has no visual effect when the footer slot is empty (slot is hidden in that case).
 * @cssprop [--n-navigation-footer-padding-inline=var(--n-navigation-padding-inline)] - Inline-axis (start/end) padding inside the footer slot when `sticky-footer` is set. Defaults to `--n-navigation-padding-inline` so the footer aligns with the nav content unless overridden. Has no visual effect when the footer slot is empty.
 */
@customElement('nord-navigation')
export default class Navigation extends LitElement {
  static styles = [componentStyle, style]

  private headerSlot = new SlotController(this, 'header')
  private events = new EventController(this)
  private footerObserver: MutationObserver | null = null
  private layoutObserver?: MutationObserver
  private observedLayout?: HTMLElement | null
  private allowItemsToRemainOpen = false

  /**
   * Controls whether the navigations's footer has sticky positioning.
   */
  @property({ type: Boolean, reflect: true, attribute: 'sticky-footer' }) stickyFooter: boolean = false

  /**
   * Opt-in: when set, any open rail-mode subnav flyout in this navigation
   * closes automatically once one of its leaf items is activated (for example,
   * when a user clicks a link inside a flyout). Off by default — existing
   * flyout behavior is unchanged unless this attribute is present.
   */
  @property({ type: Boolean, reflect: true, attribute: 'auto-dismiss-on-select' }) autoDismissOnSelect: boolean = false

  /**
   * Closes every open rail-mode subnav flyout within this navigation.
   *
   * Each parent `<nord-nav-item>` renders its rail flyout inside its own shadow
   * DOM, so a leaf link click inside a flyout does not dismiss it. Wire a single
   * root-level handler that calls this method to close any open flyout without
   * reaching into component shadow DOM.
   *
   * Returns a promise that resolves once every flyout has finished closing.
   */
  async closeAllFlyouts(): Promise<void> {
    const items = this.querySelectorAll<NavItem>('nord-nav-item')
    await Promise.all(Array.from(items, item => item.closeFlyout()))
  }

  connectedCallback() {
    super.connectedCallback()
    // if we add this event listener during render
    // we seem to miss the first activate event.
    // so let's add it as early as possible.
    this.events.listen(this, 'activate', this.handleActivate)

    // in cases where there is an initially active nav-item,
    // we should allow any nav-items with the `open` attribute to remain open.
    const activeItem = this.querySelector('nord-nav-item[active]')
    this.allowItemsToRemainOpen = activeItem != null

    if (typeof window !== 'undefined') {
      this.observeFooter()
    }
    this.startObservingLayout()
    railMediaQuery.addEventListener('change', this.handleViewportChange)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.footerObserver?.disconnect()
    this.footerObserver = null
    this.stopObservingLayout()
    railMediaQuery.removeEventListener('change', this.handleViewportChange)
  }

  private handleViewportChange = () => this.updateRailState()

  private startObservingLayout() {
    this.observedLayout = this.closest<HTMLElement>('nord-layout')
    if (!this.observedLayout) {
      return
    }
    this.updateRailState()
    this.layoutObserver = new MutationObserver(() => this.updateRailState())
    this.layoutObserver.observe(this.observedLayout, {
      attributes: true,
      attributeFilter: ['nav-open', 'collapse-mode'],
    })
  }

  private stopObservingLayout() {
    this.layoutObserver?.disconnect()
    this.layoutObserver = undefined
    this.observedLayout = undefined
    this.toggleAttribute('data-rail-collapsed', false)
  }

  private updateRailState() {
    if (!this.observedLayout) {
      return
    }
    const next = railMediaQuery.matches
      && this.observedLayout.getAttribute('collapse-mode') === 'rail'
      && !this.observedLayout.hasAttribute('nav-open')
    this.toggleAttribute('data-rail-collapsed', next)
  }

  /**
   * Observe the footer slot element for visibility changes.
   * Watches style and class attributes on the footer element itself, and
   * childList on the host to detect footer being added/removed (e.g. v-if).
   */
  private observeFooter() {
    this.footerObserver = new MutationObserver(() => this.requestUpdate())

    // Watch for footer element being added/removed from the host
    this.footerObserver.observe(this, { childList: true })

    // Watch hidden attribute on the footer element directly
    const footerEl = this.querySelector(':scope > [slot="footer"]')
    if (footerEl) {
      this.footerObserver.observe(footerEl, {
        attributes: true,
        attributeFilter: ['style', 'class'],
      })
    }
  }

  private get isFooterVisible() {
    // In SSR, the element is backed by a minimal DOM shim that does not implement
    // querySelector or window.getComputedStyle. Return true so the footer slot
    // renders as visible during server-side rendering.
    if (typeof window === 'undefined') {
      return true
    }

    const footerEl = this.querySelector<HTMLElement>(':scope > [slot="footer"]')
    if (!footerEl) {
      return false
    }

    const style = window.getComputedStyle(footerEl)
    return (
      style.display !== 'none'
      && style.visibility !== 'hidden'
      && style.visibility !== 'collapse'
      && style.opacity !== '0'
      && style.contentVisibility !== 'hidden'
    )
  }

  render() {
    return html`
      <slot name="header" ?hidden=${this.headerSlot.isEmpty}></slot>
      <div class="n-navigation-main">
        <nav>
          <slot></slot>
        </nav>
        ${!this.stickyFooter ? html`<slot name="footer" ?hidden=${!this.isFooterVisible}></slot>` : nothing}
      </div>
      ${this.stickyFooter ? html`<slot name="footer" ?hidden=${!this.isFooterVisible}></slot>` : nothing}
    `
  }

  private handleActivate = (e: Event) => {
    const target = e.target as NavItem
    const ancestors = new Set<NavItem>()

    // walk up the DOM, gathering all ancestors from the target to its top-most nav-item.
    // it's more efficient to first gather ancestors here,
    // than to repeatedly call item.contains(target) in the for-loop below.
    let parent = target.parentElement?.closest('nord-nav-item')
    while (parent) {
      ancestors.add(parent)
      parent = parent.parentElement?.closest('nord-nav-item')
    }

    // open and activate any items on the path, otherwise close and deactivate.
    // however, if the item is set to open initially, we should not close it.
    for (const item of this.querySelectorAll('nord-nav-item')) {
      if (item !== target) {
        const isAncestor = ancestors.has(item)
        item.open = isAncestor || (this.allowItemsToRemainOpen && item.open)
        item.active = isAncestor
      }
    }

    // this is a one-time thing, purely for initial open state
    this.allowItemsToRemainOpen = false

    // opt-in: close any open rail flyout once a leaf item is selected.
    if (this.autoDismissOnSelect) {
      void this.closeAllFlyouts()
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-navigation': Navigation
  }
}
